import "../../css/page/newProduct.css";
import React, { useContext, useState } from "react";
import axiosInstance from "../../services/axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { endpoint } from "../../utils/API";
import { InputAdornment, InputLabel, TextField } from "@mui/material";
import { CalculateSharp } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import { Add, Refresh, TwoWheeler } from "@material-ui/icons";
import { FormContext } from "../../contexts/FormContext";
import { TEMPLATE_PRODUCT } from "../../templates";
import { handleOnKeyDownEnter } from "utils/misc";
import {
  computeMargin,
  computePurchasePrice,
  computeSellingPrice,
  computeSellingPriceWithTaxes,
} from "utils/Forms";

const NewVehicle = () => {
  const navigate = useNavigate();
  const { form, setForm } = useContext(FormContext);
  const [isItemNumberExists, setIsItemNumberExists] = useState(false);

  const updateForm = (e) => {
    const field = {
      name: e.target.name,
      value: e.target.value,
    };

    setForm({
      ...form,
      vehicle: { ...form.vehicle, [field.name]: field.value },
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const newProduct = {
      brand: form.vehicle.brand,
      itemNumber: form.vehicle.itemNumber,
      name: form.vehicle.name,
      description: form.vehicle.description,
      stock: Number.parseInt(form.vehicle.stock),
      purchasePrice: Number.parseFloat(form.vehicle.purchasePrice),
      sellingPrice: Number.parseFloat(form.vehicle.sellingPrice),
      sellingPriceWithTaxes: Number.parseFloat(
        form.vehicle.sellingPriceWithTaxes
      ),
      margin: Number.parseFloat(form.vehicle.margin),
      vatPercentage: Number.parseFloat(form.vehicle.vatPercentage),
      localization: form.vehicle.localization,
    };

    await axiosInstance
      .post(`${endpoint}/vehicles`, newProduct)
      .then(() => {
        toast.success("Véhicule ajouté avec succès !");
        setForm({ ...form, vehicle: { ...TEMPLATE_PRODUCT } });
        navigate("/vehicles");
      })
      .catch(() =>
        toast.error("Une erreur est survenue lors de l'ajout du véhicule !")
      );
  };

  const handleComputePurchasePrice = () => {
    const purchasePrice = computePurchasePrice(form.vehicle);

    setForm({
      ...form,
      vehicle: { ...form.vehicle, purchasePrice: purchasePrice },
    });
  };

  const handleComputeSellingPrice = () => {
    const sellingPrice = computeSellingPrice(form.vehicle);

    setForm({
      ...form,
      vehicle: { ...form.vehicle, sellingPrice: sellingPrice },
    });
  };

  const handleComputeMargin = () => {
    const margin = computeMargin(form.vehicle);

    setForm({
      ...form,
      vehicle: { ...form.vehicle, margin: margin },
    });
  };

  const handleComputeSellingPriceWithTaxes = () => {
    const sellingPriceWithTaxes = computeSellingPriceWithTaxes(form.vehicle);

    setForm({
      ...form,
      vehicle: {
        ...form.vehicle,
        sellingPriceWithTaxes: sellingPriceWithTaxes,
      },
    });
  };

  const resetForm = () => {
    setForm({ ...form, vehicle: { ...TEMPLATE_PRODUCT } });
  };

  const checkItemNumberExists = async () => {
    setIsItemNumberExists(false);
    const itemNumber = form.vehicle.itemNumber;

    if (itemNumber) {
      await axiosInstance
        .get(`${endpoint}/vehicles/find/${itemNumber}`)
        .then((response) => {
          if (response.data.result) {
            setIsItemNumberExists(true);
          }
        })
        .catch(() => {});
    }
  };

  return (
    <div className="content">
      <div className="above-table">
        <div className="title-refresh-container">
          <h1 className="title">Nouveau véhicule</h1>
          <button onClick={resetForm} className="refresh-button">
            <div className="homeLinkIcons">
              <Refresh />
            </div>
          </button>
        </div>
      </div>

      <form
        className="addProductForm"
        onSubmit={onSubmit}
        onKeyDown={handleOnKeyDownEnter}
      >
        <div className="addProductItem">
          <InputLabel htmlFor="brand">Marque</InputLabel>
          <TextField
            type="text"
            value={form.vehicle.brand}
            name="brand"
            onChange={updateForm}
            placeholder="BIR"
            inputProps={{
              className: "upperCase",
            }}
          />
        </div>
        <div className="addProductItem">
          <InputLabel htmlFor="brand">Référence</InputLabel>
          <TextField
            type="text"
            value={form.vehicle.itemNumber}
            name="itemNumber"
            onChange={updateForm}
            placeholder="motul-100000"
            onBlur={checkItemNumberExists}
            error={isItemNumberExists}
            helperText={
              isItemNumberExists
                ? "Cette référence a déjà été assignée !"
                : "Référence disponible !"
            }
            inputProps={{
              className: "upperCase",
            }}
          />
        </div>
        <div className="addProductItem">
          <InputLabel htmlFor="name" required>
            Nom
          </InputLabel>
          <TextField
            type="text"
            value={form.vehicle.name}
            name="name"
            onChange={updateForm}
            placeholder="Apple Airpods"
            required
          />
        </div>
        <div className="addProductItem">
          <InputLabel htmlFor="description">Description</InputLabel>
          <TextField
            type="text"
            value={form.vehicle.description}
            name="description"
            onChange={updateForm}
            placeholder="Écouteurs de la marque Apple"
          />
        </div>
        <div className="addProductItem">
          <InputLabel htmlFor="stock" required>
            Quantité en stock
          </InputLabel>
          <TextField
            type="number"
            value={form.vehicle.stock}
            name="stock"
            onChange={updateForm}
            required
          />
        </div>
        <div className="addProductItem">
          <InputLabel htmlFor="purchasePrice" required>
            Prix d'achat unitaire
          </InputLabel>
          <TextField
            inputProps={{ step: "0.01" }}
            type="number"
            value={form.vehicle.purchasePrice}
            name="purchasePrice"
            onChange={updateForm}
            InputProps={{
              endAdornment: (
                <InputAdornment position={"end"}>
                  <IconButton onClick={handleComputePurchasePrice}>
                    <CalculateSharp />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            required
          />
        </div>
        <div className="addProductItem">
          <InputLabel htmlFor="sellingPrice" required>
            Prix de vente unitaire
          </InputLabel>
          <TextField
            inputProps={{ step: "0.01" }}
            type="number"
            value={form.vehicle.sellingPrice}
            name="sellingPrice"
            onChange={updateForm}
            InputProps={{
              endAdornment: (
                <InputAdornment position={"end"}>
                  <IconButton onClick={handleComputeSellingPrice}>
                    <CalculateSharp />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            required
          />
        </div>
        <div className="addProductItem">
          <InputLabel htmlFor="margin" required>
            Marge de base (%)
          </InputLabel>
          <TextField
            inputProps={{ step: "0.01" }}
            type="number"
            value={form.vehicle.margin}
            name="margin"
            onChange={updateForm}
            InputProps={{
              endAdornment: (
                <InputAdornment position={"end"}>
                  <IconButton onClick={handleComputeMargin}>
                    <CalculateSharp />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            required
          />
        </div>
        <div className="addProductItem">
          <InputLabel htmlFor="vatPercentage">Taux T.V.A. (%)</InputLabel>
          <TextField
            inputProps={{ step: "0.01" }}
            type="number"
            value={form.vehicle.vatPercentage}
            name="vatPercentage"
            onChange={updateForm}
            placeholder="21"
            required
          />
        </div>
        <div className="addProductItem">
          <InputLabel htmlFor="sellingPriceWithTaxes" required>
            Prix de vente unitaire TTC
          </InputLabel>
          <TextField
            inputProps={{ step: "0.01" }}
            type="number"
            value={form.vehicle.sellingPriceWithTaxes}
            name="sellingPriceWithTaxes"
            onChange={updateForm}
            InputProps={{
              endAdornment: (
                <InputAdornment position={"end"}>
                  <IconButton onClick={handleComputeSellingPriceWithTaxes}>
                    <CalculateSharp />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            required
          />
        </div>
        <div className="addProductItem">
          <InputLabel htmlFor="localization">Localisation</InputLabel>
          <TextField
            type="text"
            value={form.vehicle.localization}
            name="localization"
            onChange={updateForm}
            placeholder="H00103"
            inputProps={{
              className: "upperCase",
            }}
          />
        </div>
        <button type="submit" className="create-button">
          <div className="homeLinkIcons">
            <Add />
            <TwoWheeler />
          </div>
          <span>Véhicule</span>
        </button>
      </form>
    </div>
  );
};

export default NewVehicle;
